import { supabase } from '@/lib/supabase';
import { analyzeRows, type ParsedDataset } from '@/lib/biData';

// ============================================================
// Zifek BI — sources internes : toute la boutique devient
// analysable (ventes, produits, services, clients, factures,
// annonces). Chaque source est convertie au même format que
// les fichiers importés, donc elle profite des mêmes filtres,
// graphiques et du chat IA.
// ============================================================

export type StoreSourceKey = 'ventes' | 'produits' | 'services' | 'clients' | 'factures' | 'annonces';

export interface StoreSourceDef {
  key: StoreSourceKey;
  label: string;
  icon: string;
  description: string;
}

export const STORE_SOURCES: StoreSourceDef[] = [
  { key: 'ventes', label: 'Ventes & commandes', icon: 'ri-shopping-bag-3-line', description: 'Commandes, montants, paiements' },
  { key: 'produits', label: 'Catalogue produits', icon: 'ri-archive-2-line', description: 'Prix, stock, statut' },
  { key: 'services', label: 'Services', icon: 'ri-service-line', description: 'Prix, vues, ville' },
  { key: 'clients', label: 'Clients', icon: 'ri-user-3-line', description: 'Base clients de la boutique' },
  { key: 'factures', label: 'Factures & devis', icon: 'ri-file-text-line', description: 'Facturé, encaissé, restant' },
  { key: 'annonces', label: 'Annonces', icon: 'ri-megaphone-line', description: 'Publication et visibilité' },
];

function num(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const n = Number(String(value ?? '').replace(/[^\d.,-]/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

function dateOnly(value: unknown): string {
  if (!value) return '';
  const d = new Date(String(value));
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

function toDataset(headers: string[], rows: unknown[][]): ParsedDataset {
  return analyzeRows(headers, rows);
}

/** Construit la source « boutique » demandée à partir de la base. */
export async function buildStoreDataset(key: StoreSourceKey, commerceId: number): Promise<{ name: string; dataset: ParsedDataset }> {
  if (!commerceId) throw new Error('Commerce introuvable pour charger vos données.');

  if (key === 'ventes') {
    const { data, error } = await supabase
      .from('commande')
      .select('id, titre, nomclient, date_time, sp, sommepayee, restant, monaie, methodepayment, typecommande, details')
      .eq('idvendeur', commerceId)
      .order('date_time', { ascending: false })
      .limit(5000);
    if (error) throw error;    const rows = (data || []).map((o) => [
      Number(o.id) || 0,
      o.titre || 'Commande',
      o.nomclient || 'Client',
      dateOnly(o.date_time),
      num(o.sp),
      num(o.sommepayee),
      o.restant != null ? num(o.restant) : Math.max(0, num(o.sp) - num(o.sommepayee)),
      o.monaie || 'MAD',
      o.methodepayment || 'Non précisé',
      o.typecommande || 'Produit',
    ]);
    return {
      name: 'Boutique · Ventes & commandes',
      dataset: toDataset(['Numéro', 'Titre', 'Client', 'Date', 'Montant', 'Montant payé', 'Restant', 'Devise', 'Paiement', 'Type'], rows),
    };
  }

  if (key === 'produits') {
    const { data, error } = await supabase
      .from('product_items')
      .select('id, name, price, stock, status, created_at')
      .order('created_at', { ascending: false })
      .limit(5000);
    if (error) throw error;
    const rows = (data || []).map((p) => [
      String(p.name || 'Produit'),
      num(p.price),
      Number(p.stock) || 0,
      p.status || 'draft',
      dateOnly(p.created_at),
    ]);
    return {
      name: 'Boutique · Catalogue produits',
      dataset: toDataset(['Produit', 'Prix', 'Stock', 'Statut', 'Créé le'], rows),
    };
  }

  if (key === 'services') {
    const { data, error } = await supabase
      .from('services')
      .select('id, titre, prix, vue, ville, pays, typeservice, created_at')
      .eq('idvendeur', commerceId)
      .order('created_at', { ascending: false })
      .limit(5000);
    if (error) throw error;
    const rows = (data || []).map((s) => [
      s.titre || 'Service',
      num(s.prix),
      Number(s.vue) || 0,
      s.ville || '',
      s.pays || '',
      s.typeservice || 'Service',
      dateOnly(s.created_at),
    ]);
    return {
      name: 'Boutique · Services',
      dataset: toDataset(['Service', 'Prix', 'Vues', 'Ville', 'Pays', 'Type', 'Créé le'], rows),
    };
  }

  if (key === 'clients') {
    const { data, error } = await supabase
      .from('mesclients')
      .select('id, idclient, datedajout')
      .eq('idshop', commerceId)
      .order('datedajout', { ascending: false })
      .limit(5000);
    if (error) throw error;
    const rows = (data || []).map((c) => [
      Number(c.idclient) || 0,
      dateOnly(c.datedajout),
    ]);
    return {
      name: 'Boutique · Clients',
      dataset: toDataset(['Client', 'Ajouté le'], rows),
    };
  }

  if (key === 'factures') {
    const { data, error } = await supabase
      .from('finance_invoices')
      .select('number, customer_name, doc_type, issue_date, total, amount_paid, status, currency')
      .eq('merchant_id', commerceId)
      .order('issue_date', { ascending: false })
      .limit(5000);
    if (error) throw error;
    const rows = (data || []).map((inv) => {
      const total = num(inv.total);
      const paid = num(inv.amount_paid);
      return [
        inv.number || '—',
        inv.customer_name || 'Client',
        inv.doc_type === 'quote' ? 'Devis' : 'Facture',
        dateOnly(inv.issue_date),
        total,
        paid,
        Math.max(0, total - paid),
        inv.status || 'draft',
        inv.currency || 'MAD',
      ];
    });
    return {
      name: 'Boutique · Factures & devis',
      dataset: toDataset(['Numéro', 'Client', 'Type', 'Date', 'Total', 'Payé', 'Restant', 'Statut', 'Devise'], rows),
    };
  }

  // annonces
  const { data, error } = await supabase
    .from('annonce')
    .select('id, titre, type, prix, ville, pays, click, publique, created')
    .eq('idvendeur', commerceId)
    .order('created', { ascending: false })
    .limit(5000);
  if (error) throw error;
  const rows = (data || []).map((a) => [
    a.titre || 'Annonce',
    a.type || '—',
    num(a.prix),
    a.ville || '',
    a.pays || '',
    num(a.click),
    a.publique ? 'Publiée' : 'Brouillon',
    dateOnly(a.created),
  ]);
  return {
    name: 'Boutique · Annonces',
    dataset: toDataset(['Annonce', 'Type', 'Prix', 'Ville', 'Pays', 'Clics', 'Statut', 'Créée le'], rows),
  };
}